import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listStories, deleteStory } from '../api/client';
import type { StoryResponse } from '../api/types';

export default function StoriesPage() {
  const [stories, setStories] = useState<StoryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    listStories(100, 0)
      .then((res) => setStories(res.stories))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (e: React.MouseEvent, storyId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!confirm('Delete this story? This will also delete all associated matches and vectors.')) {
      return;
    }

    setDeletingId(storyId);
    try {
      await deleteStory(storyId);
      setStories((prev) => prev.filter((s) => s.id !== storyId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete story');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading stories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Stories</h1>
        <span className="text-gray-500">{stories.length} total</span>
      </div>

      <div className="grid gap-4">
        {stories.map((story) => (
          <Link
            key={story.id}
            to={`/stories/${story.id}`}
            className="card hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-pebbl-100 text-pebbl-700">
                    {story.type}
                  </span>
                  {story.location.venue && (
                    <span className="text-xs text-gray-500">
                      {story.location.venue}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {story.location.city}
                  </span>
                </div>
                <p className="text-gray-700 line-clamp-2">{story.rawText}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  {story.time.date && (
                    <span>Date: {story.time.date}</span>
                  )}
                  {story.time.dayPart && (
                    <span>{story.time.dayPart}</span>
                  )}
                  {story.event.trigger && (
                    <span>Trigger: {story.event.trigger}</span>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-gray-400 ml-4 shrink-0 flex flex-col items-end gap-2">
                <div>
                  <div className="font-mono">{story.id.slice(0, 8)}...</div>
                  <div>{new Date(story.createdAt).toLocaleDateString()}</div>
                </div>
                <button
                  onClick={(e) => handleDelete(e, story.id)}
                  disabled={deletingId === story.id}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded text-xs disabled:opacity-50"
                >
                  {deletingId === story.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </Link>
        ))}

        {stories.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No stories found. Submit some stories via the API first.
          </div>
        )}
      </div>
    </div>
  );
}
